import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';
import { OnboardingRecord, CreateOnboardingRecord, OnboardingTask, WelcomePackItem, EmailTemplate, CreateEmailTemplate, OnboardingTaskTemplate, CreateOnboardingTaskTemplate, OnboardingCategory, OnboardingCategoryOrderItem } from '../models/onboarding.model';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private apiUrl = `${environment.apiUrl}/Onboarding`;
  private emailTemplateUrl = `${environment.apiUrl}/EmailTemplate`;

  constructor(private http: HttpClient) {}

  // Onboarding Records
  getAllRecords(status?: string): Observable<OnboardingRecord[]> {
    if (status) {
      return this.http.get<OnboardingRecord[]>(this.apiUrl, { params: { status } }).pipe(retry(2));
    }
    return this.http.get<OnboardingRecord[]>(this.apiUrl).pipe(retry(2));
  }

  getRecordById(id: string): Observable<OnboardingRecord> {
    return this.http.get<OnboardingRecord>(`${this.apiUrl}/${id}`).pipe(retry(2));
  }

  createRecord(record: CreateOnboardingRecord): Observable<OnboardingRecord> {
    return this.http.post<OnboardingRecord>(this.apiUrl, record);
  }

  updateTasks(recordId: string, tasks: { id: string; isCompleted: boolean }[]): Observable<any> {
    return this.http.put(`${this.apiUrl}/${recordId}/tasks`, { tasks }, { responseType: 'text' });
  }

  updateWelcomePack(recordId: string, items: WelcomePackItem[]): Observable<any> {
    return this.http.put(`${this.apiUrl}/${recordId}/welcome-pack`, items, { responseType: 'text' });
  }

  sendEmail(recordId: string, templateType: string = 'Welcome'): Observable<any> {
    return this.http.post(`${this.apiUrl}/${recordId}/send-email`, {}, { params: { templateType }, responseType: 'text' });
  }

  setWorkEmail(recordId: string, email: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${recordId}/work-email`, { email }, { responseType: 'text' });
  }

  deleteRecord(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, { responseType: 'text' });
  }

  // Task Templates
  getAllTaskTemplates(): Observable<OnboardingTaskTemplate[]> {
    return this.http.get<OnboardingTaskTemplate[]>(`${this.apiUrl}/templates`).pipe(retry(2));
  }

  createTaskTemplate(template: CreateOnboardingTaskTemplate): Observable<OnboardingTaskTemplate> {
    return this.http.post<OnboardingTaskTemplate>(`${this.apiUrl}/templates`, template);
  }

  updateTaskTemplate(id: string, template: OnboardingTaskTemplate): Observable<OnboardingTaskTemplate> {
    return this.http.put<OnboardingTaskTemplate>(`${this.apiUrl}/templates/${id}`, template);
  }

  deleteTaskTemplate(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/templates/${id}`, { responseType: 'text' });
  }

  seedDefaultTemplates(): Observable<any> {
    return this.http.post(`${this.apiUrl}/templates/seed-defaults`, {}, { responseType: 'text' });
  }

  // Adds any missing default-template tasks to an existing record (preserves existing states).
  syncTasksFromTemplates(recordId: string): Observable<{ added: number }> {
    return this.http.post<{ added: number }>(`${this.apiUrl}/${recordId}/sync-tasks`, {});
  }

  // Onboarding Categories (orderable tabs + P&C sub-categories)
  getCategories(): Observable<OnboardingCategory[]> {
    return this.http.get<OnboardingCategory[]>(`${this.apiUrl}/categories`).pipe(retry(2));
  }

  reorderCategories(items: OnboardingCategoryOrderItem[]): Observable<OnboardingCategory[]> {
    return this.http.put<OnboardingCategory[]>(`${this.apiUrl}/categories/reorder`, items);
  }

  // Email Templates
  getAllTemplates(): Observable<EmailTemplate[]> {
    return this.http.get<EmailTemplate[]>(this.emailTemplateUrl).pipe(retry(2));
  }

  getTemplateById(id: string): Observable<EmailTemplate> {
    return this.http.get<EmailTemplate>(`${this.emailTemplateUrl}/${id}`).pipe(retry(2));
  }

  createTemplate(template: CreateEmailTemplate): Observable<EmailTemplate> {
    return this.http.post<EmailTemplate>(this.emailTemplateUrl, template);
  }

  updateTemplate(id: string, template: EmailTemplate): Observable<EmailTemplate> {
    return this.http.put<EmailTemplate>(`${this.emailTemplateUrl}/${id}`, template);
  }

  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.emailTemplateUrl}/${id}`);
  }

  seedDefaultEmailTemplates(): Observable<any> {
    return this.http.post(`${this.emailTemplateUrl}/seed-defaults`, {}, { responseType: 'text' });
  }
}
